/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

/*
 * Colour parsing, for deciding what reads legibly on a given background.
 */

/**
 * Parses a CSS color string into an `[r, g, b]` tuple (0–255 each).
 * Supports `rgb(…)` / `rgba(…)` and hex notation (`#RGB`, `#RRGGBB`).
 * Returns `[NaN, NaN, NaN]` when the string is not a recognized format.
 * @param {string} color - A CSS color string.
 * @returns {[number, number, number]}
 */
export const parseColor = (color) => {
    let r;
    let g;
    let b;

    if (/^rgb/.test(color)) {
        const match = color.match(
            /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/
        );
        if (!match) return [Number.NaN, Number.NaN, Number.NaN];
        r = +match[1];
        g = +match[2];
        b = +match[3];
    } else if (/^#/.test(color)) {
        const rgb = +`0x${color.slice(1).replace(color.length < 5 && /./g, '$&$&')}`;

        r = rgb >> 16;
        g = (rgb >> 8) & 255;
        b = rgb & 255;
    }

    return [r, g, b];
};

/**
 * Returns `true` if the given color is perceptually dark (luminance below 50%).
 * Uses the HSP color model (human-eye sensitivity weighting).
 * @param {string|[number, number, number]} color - A CSS color string or an `[r, g, b]` tuple.
 * @returns {boolean}
 */
export const isDarkColor = (color) => {
    let r;
    let g;
    let b;

    if (typeof color === 'string' || color instanceof String) {
        [r, g, b] = parseColor(color);
    } else {
        [r, g, b] = color;
    }

    const hsp = 0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b);

    return hsp <= 16129;
};
